import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { calculateJobKPIs, JobHubJob } from "@/lib/jobUtils";
import { Edit, AlertCircle, LucideIcon } from "lucide-react";

// Import dashboard icons for consistency
import iconTotalReferrals from "@/assets/dashboard/icon_totalreferrals.svg";
import iconInterviewing from "@/assets/dashboard/icon_interviewing.svg";

interface CustomKPI {
  title: string;
  value: string;
  subtitle?: string;
  icon: string | LucideIcon;
  variant?: "default" | "dark";
}

interface JobKPICardsProps {
  jobs: JobHubJob[];
  loading?: boolean;
  customKPIs?: CustomKPI[];
}

interface KPICardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: string | LucideIcon; // Support both SVG file path and Lucide icon
  variant?: "default" | "dark";
  loading?: boolean;
}

const KPICard = ({ title, value, subtitle, icon, variant = "default", loading }: KPICardProps) => {
  if (loading) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <Skeleton className="h-8 w-16 mb-2" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="w-10 h-10 rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const isDark = variant === "dark";
  
  return (
    <Card 
      className={`border-0 shadow-sm ${isDark ? "text-white" : ""}`}
      style={isDark ? { backgroundColor: '#2D2D2D' } : {}}
      data-testid={`kpi-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent 
        className="p-0"
        style={{
          display: 'flex',
          padding: '16px',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '8px'
        }}
      >
        {typeof icon === 'string' ? (
          <img 
            src={icon} 
            alt={title}
            className="h-6 w-6 sm:h-7 sm:w-7"
          />
        ) : (
          React.createElement(icon, { 
            className: `h-5 w-5 sm:h-6 sm:w-6 ${
              isDark ? "text-white" : 
              title === "Draft Jobs" ? "text-[#FF7A00]" : "text-gray-900"
            }` 
          })
        )}
        <div className="flex items-baseline gap-1">
          <p className={`text-2xl sm:text-3xl font-bold space-grotesk ${
            isDark ? "text-white" : "text-gray-900"
          }`}>
            {value}
          </p>
          {subtitle && (
            <p className={`text-lg space-grotesk ${
              isDark ? "text-gray-400" : "text-gray-500"
            }`}>
              {subtitle}
            </p>
          )}
        </div>
        <p className={`text-xs font-medium uppercase space-grotesk leading-tight tracking-[0.07px] ${
          isDark ? "text-gray-400" : "text-gray-500"
        }`}>
          {title}
        </p>
      </CardContent>
    </Card>
  );
};

export function JobKPICards({ jobs, loading, customKPIs }: JobKPICardsProps) {
  // Use custom KPIs if provided, otherwise calculate from jobs
  const kpiData = customKPIs || (() => {
    const kpis = calculateJobKPIs(jobs);
    return [
      {
        title: "Active Jobs",
        value: kpis.activeJobs.toString(),
        icon: iconInterviewing,
        variant: "default" as const,
      },
      {
        title: "Draft Jobs",
        value: kpis.draftJobs.toString(),
        icon: Edit,
        variant: "default" as const,
      },
      {
        title: "Total Applications",
        value: kpis.totalApplications.toString(),
        icon: iconTotalReferrals,
        variant: "default" as const,
      },
      {
        title: "High Priority",
        value: kpis.highPriorityJobs.toString(),
        icon: AlertCircle,
        variant: "dark" as const,
      }
    ] as CustomKPI[];
  })();

  const gridCols = customKPIs ? "lg:grid-cols-3" : "lg:grid-cols-4";
  
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-4 lg:gap-6 mb-6 lg:mb-8`}>
      {kpiData.map((kpi, index) => (
        <KPICard
          key={index}
          title={kpi.title}
          value={kpi.value}
          subtitle={kpi.subtitle}
          icon={kpi.icon}
          variant={kpi.variant}
          loading={loading}
        />
      ))}
    </div>
  );
}
